/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AuthApiService } from '../../http';
import { BaseComponent } from '../index';
import { Component, OnInit } from '@angular/core';
import {
    ConnectionDetailsService,
    LogService,
    ModalService,
    PGPService,
    SocketService,
    UserService,
    UtilsService
} from '../../services';
import { ErrorResponseModel, UserSessionModel } from '../../models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LoginModel } from '../../../modules/loadbalancer/shared/models';
import { PortalUrlsEnum } from '../../../modules/centralisedManagement/shared/enums';
import { Router } from '@angular/router';
import { TimerService } from '../../../modules/loadbalancer/shared/services/utilities';
import { TokenStatus } from '../../enums';
import { environment } from '../../../../environments/environment';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-relogin',
    templateUrl: './relogin.component.html',
    styleUrls: ['relogin.component.scss']
})

export class ReLoginComponent extends BaseComponent implements OnInit {
    /**
     * A boolean to determine if the login should show.
     */
    public showLogin: boolean = true;

    /**
     * A boolean to determine if the show connecting message should show.
     */
    public showConnecting: boolean = false;

    /**
     * The form group variable.
     */
    public reLoginForm: FormGroup;

    /**
     * Store the name of the SSO provider.
     */
    public ssoProvider: string = null;

    /**
     * Store is the user logged in with OTP.
     */
    public otpEnabled: boolean = false;

    /**
     * For testing only
     */
    public hasSSOToken: boolean = false;

    /**
     * Stored the user ID coming back from the login object.
     */
    public twoFactorUserDetails: any = null;

    /**
     * Hashed phone number from OTP service.
     */
    public twoFactorAuthPhoneNumber: string = '';

    constructor(
        private authHttpService: AuthApiService,
        private connectionDetailsService: ConnectionDetailsService,
        private formBuilder: FormBuilder,
        private logService: LogService,
        private modalService: ModalService,
        private pgpService: PGPService,
        private socketService: SocketService,
        private router: Router,
        private timerService: TimerService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();

        this.setupForm();
    }

    public ngOnInit(): void {
        this.setUpTokenChecking();
    }

    /**
     * Logout of the user interface.
     */
    public logout(): void {
        this.timerService.flushIntervals();
        this.userService.logout();
    }

    /**
     * Submit for for re-login.
     */
    public submitReLogin(): void {
        this.showLogin = false;
        this.showConnecting = true;

        const user = this.userService.getUser();
        const userDetails = this.formatUserDetails(user);
        userDetails.password = this.getUserPasswordModuleDepending();

        if (this.userDetailsInvalid(userDetails)) {
            this.userService.logout();
        } else {
            const timerId = setTimeout(() => {
                this.authHttpService.login(userDetails)
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe({
                        next: (data: any) => {
                            if (data.otpEnabled) {
                                this.enableTwoFactorAuthStep(userDetails, data);
                            } else {
                                this.completeReLogin(userDetails, data);
                            }
                        },
                        error: (error: ErrorResponseModel) => {
                            if (error.message === 'Verify host certificate authority') {
                                this.modalService.close('relogin');
                                this.modalService.openModalInParent('certExpiredLogout');
                            } else {
                                this.reLoginForm.get('password').setErrors({ 'incorrectPassword': error.message });
                            }

                            this.showLogin = true;
                            this.showConnecting = false;

                            this.logService.debug('Auth Failure', error.message);
                        },
                        complete: () => {
                            clearTimeout(timerId);
                        }
                    });
            }, 2000);
        }
    }

    /**
     * Login the user in with new token from two-factor auth service.
     */
    public logUserInWithToken($event: any): void {
        const data = $event;
        if (data && data.token) {
            const decodedToken = this.utilsService.decodeJWT(data.token);
            const userDetails = this.createUserDetailsFromToken(decodedToken);
            this.completeReLogin(userDetails, data);
        }
    }

    /**
     * Create user details from token.
     * @param decodedToken
     * @private
     */
    public createUserDetailsFromToken(decodedToken: any): any {
        return {
            email: decodedToken.email,
            applianceAddress: this.connectionDetailsService.getAddress(),
            appliancePort: this.connectionDetailsService.getPort(),
            ssoLogin: this.hasSSOToken,
            otpEnabled: this.twoFactorUserDetails.otpEnabled
        };
    }

    /**
     * Lookup SSO provider name.
     */
    public getSsoProviderName(): string {
        switch (this.ssoProvider) {
            case 'google':
                return 'Google';
            case 'azureadv2':
                return 'Azure AD';
            case 'saml':
                return 'SAML';
            default:
                return this.ssoProvider;
        }
    }

    /**
     * Redirect user to SSO sign in.
     */
    public linkToSSOProvider(): void {
        const brand = this.ssoProvider;
        this.userService.setUserValueByProperty('ssoProvider', brand);
        window.location.href = environment.production
            ? '/api/v1/accounts/auth/' + brand
            : PortalUrlsEnum.NON_PRODUCTION + '/api/v1/accounts/auth/' + brand;
    }

    /**
     * Send two-factor auth code.
     * @param userDetails
     * @param data
     * @private
     */
    private enableTwoFactorAuthStep(userDetails: LoginModel, data: {
        id: any;
        otpEnabled: boolean;
        hashedNumber: string;
    }): void {
        this.twoFactorUserDetails = userDetails;
        this.twoFactorUserDetails.userId = data.id;
        this.twoFactorUserDetails.otpEnabled = data.otpEnabled;
        this.twoFactorAuthPhoneNumber = data.hashedNumber;
        this.otpEnabled = data.otpEnabled;
        this.showLogin = true;
        this.showConnecting = false;
    }

    /**
     * Complete relogin.
     * @param userDetails
     * @param data
     * @private
     */
    private completeReLogin(userDetails: LoginModel, data: any): void {
        this.userService.setupUserDetails(userDetails, data);
        this.modalService.close('relogin');
        this.reLoginForm.reset();
        this.setUpTokenChecking();
        this.socketService.connect();
        this.userService.updateUserLocalStorage();
    }

    /**
     * Set up the re-login form.
     * @private
     */
    private setupForm(): void {
        this.reLoginForm = this.formBuilder.group({
            password: ['', [Validators.required]]
        });
    }

    /**
     * Set the user details depending on the current module.
     * @private
     */
    private getUserPasswordModuleDepending(): string {
        if (this.environment.module === this.modulesEnum.CENTRALISED_MANAGEMENT) {
            // Encode their password
            return this.pgpService.hashPassword(this.reLoginForm.value.password);
        } else {
            return this.reLoginForm.value.password;
        }
    }

    /**
     * Format the user details for login.
     * @param user
     * @private
     */
    private formatUserDetails(user: UserSessionModel): LoginModel {
        const userDetails: LoginModel = {};

        userDetails.email = user.email;
        userDetails.applianceAddress = user.applianceAddress;
        userDetails.appliancePort = user.appliancePort;
        userDetails.password = this.reLoginForm.value.password;

        return userDetails;
    }

    /**
     * Validate user details to ensure that we have the details to be able to reconnect.
     * @param userDetails
     * @private
     */
    private userDetailsInvalid(userDetails: LoginModel): boolean {
        return !userDetails.email
            || !userDetails.applianceAddress
            || !userDetails.appliancePort;
    }

    /**
     * Set checking the token every 10 seconds to see if expired.
     * @private
     */
    private setUpTokenChecking(): void {
        // set up checking token
        this.timerService.addInterval(
            'TokenCheck',
            () => {
                this.logService.debug(
                    'Check Token Status',
                    'Check again in ' + this.userService.getTokenCheckInterval() + ' seconds'
                );

                if (this.userService.tokenStatus() === TokenStatus.EXPIRED) {
                    this.openReLoginModal();
                }
            },
            this.userService.getTokenCheckInterval()
        );
    }

    /**
     * Modal for opening the logout confirmation modal.
     * @private
     */
    private openReLoginModal(): void {
        // don't show if already on the login page
        if (!this.router.url.includes('login')) {
            this.ssoProvider = this.userService.getUser('ssoProvider');
            if (this.ssoProvider) {
                this.reLoginForm.disable();
            }
            this.timerService.flushIntervals();
            this.showLogin = true;
            this.showConnecting = false;
            this.modalService.closeAll();
            this.modalService.openModalInParent('relogin');
        }
    }
}
